import moment from 'moment';
import uuid from 'uuid/v4';
import isArray from 'lodash/isArray';
import { categories } from './components/data/trucksCategories';
import {
  QUOTING_TYPES,
  MAX_NUMBER_OF_RESULTS,
  GOODS_LOADING_DURATION,
  GOODS_UNLOADING_DURATION,
  PREDEFINED_LOADS,
} from './constants';

const truckCategories = categories.reduce((output, category) => {
  output[category._id] = category;
  return output;
}, {});

export const getFormattedSearchSummary = (
  quotingOptions,
  sender,
  receivers,
  pickup,
  dropoff,
  currency,
) => {
  try {
    const {
      // equipment: { capacity, type: equipmentType },
      value,
      shipmentItemSummary,
      type,
    } = quotingOptions;
    const [receiver] = receivers;
    // Hack TODO UPDATE
    const dropoffDate = dropoff && isArray(dropoff) ? dropoff[0].pickupDate : '23-9-2019';
    const loads = getFormattedLoadsSummary(quotingOptions, currency);
    const weight = getFormattedWeight(quotingOptions, loads);

    const output = {
      weight,
      type,
      volume: getFormattedVolume(quotingOptions),
      items: getFormattedItems(quotingOptions),
      pickup: {
        ...getFormattedPickup(pickup),
        ...sender,
        date: pickup ? pickup.pickupDate : '',
      },
      delivery: getFormattedDropoffs(
        Array.isArray(dropoff) ? dropoff : [dropoff],
        'coordinate',
        receivers,
        sender,
        quotingOptions,
      ),
      ...getTruckSpecifications(quotingOptions),

      loads,
      ...shipmentItemSummary,
      shipmentValue: getFormattedShipmentValue(quotingOptions, dropoff, currency).value,
    };
    return output;
  } catch (e) {
    console.error(e);
    return {};
  }
};

const getTruckSpecifications = (quotingOptions) => {
  const { type } = quotingOptions;

  if (QUOTING_TYPES.FTL !== type) {
    return {};
  }
  const {
    equipment: { capacity, type: equipmentType },
  } = quotingOptions;

  return {
    truckCapacity: capacity.value,
    truckType: equipmentType,
  };
};
/**
 * This function is used to get the search request schema to be sent to the server
 *
 * */
export const getFormattedSearchRequest = (
  quotingOptions,
  shipper,
  sender,
  receivers,
  pickup,
  dropoffs,
  maxResults = MAX_NUMBER_OF_RESULTS,
) => {
  const { accessorials, type, equipment } = quotingOptions;
  const { contact: senderName, phone: senderPhone } = sender;
  const [receiver] = receivers;
  const { contact: receiverName, phone: receiverPhone } = receiver;

  /**
   * @todo alter this
   */

  const equipmentType = equipment && equipment.type ? equipment.type : undefined;

  const quoteRequest = {
    maxResults,
    pickup: getFormattedPickup(pickup, 'coordinate'),
    senderId: shipper,
    sender: senderName,
    receiver: receiverName,
    receiverPhone,
    senderPhone,
    // accessorials,
    requirements: {
      fullTruck: type === QUOTING_TYPES.FTL,
      truckType: equipmentType,
    },
    specifications: [],
  };

  if (dropoffs.length > 1) {
    quoteRequest.dropoffs = getFormattedDropoffs(
      dropoffs,
      'coordinate',
      receivers,
      sender,
      quotingOptions,
    );
  } else {
    quoteRequest.dropoff = getFormattedDropoff(dropoffs[0]);
  }

  return quoteRequest;
};

export const getFormattedSearchRequestForSingleShipment = (
  quotingOptions,
  shipper,
  sender,
  receivers,
  pickup,
  dropoff,
  maxResults = MAX_NUMBER_OF_RESULTS,
  currency,
) => {
  const {
    dropoff: { load, ...dropoffRequest },
    ...restOfTheRequest
  } = getFormattedSearchRequest(
    quotingOptions,
    shipper,
    sender,
    receivers,
    pickup,
    dropoff,
    maxResults,
    currency,
  );

  const { location, ...restOfDropoffRequest } = dropoffRequest;

  return {
    ...restOfTheRequest,
    dropoff: {
      ...restOfDropoffRequest,
      coordinate: location,
    },
    load: {
      ...getFormattedLoads(quotingOptions, dropoff, currency),
      description: load.description,
      value: load.value,
    },
  };
};

export const getFormattedSearchRequestForDistributedShipment = (
  quotingOptions,
  shipper,
  sender,
  receivers,
  pickup,
  dropoffs,
  maxResults = MAX_NUMBER_OF_RESULTS,
  currency = DEFAULT_CURRENCY,
) => {
  const {
    receiver,
    receiverPhone,
    sender: senderData,
    senderPhone,
    ...rest
  } = getFormattedSearchRequest(
    quotingOptions,
    shipper,
    sender,
    receivers,
    pickup,
    dropoffs,
    maxResults,
  );

  const formattedDropOffs = rest.dropoffs.map((dropoff) => formatDropOffItem(dropoff, quotingOptions, currency));
  return { ...rest, dropoffs: formattedDropOffs };
};

const formatDropOffItem = (dropOff, qoutingOption, currency) => {
  const { equipment } = qoutingOption;

  const requirements = [];
  if (equipment && equipment.type) {
    requirements.push(equipment.type);
  }
  return {
    load: {
      ...dropOff.load,
      currency,
      description: dropOff.load.description,
      identifier: uuid('load-'),
      items: dropOff.load.items.map((item) => {
        const {
          type, dimensions, weight, quantity, stackable,
        } = item;
        const { width, length, height } = dimensions;
        const abstractType = type.startsWith('PALLET')
          ? 'PALLET'
          : type.startsWith('BOX')
            ? 'BOX'
            : 'OTHER';
        // TODO implement a util to convert and caclualte units
        return {
          type: abstractType,
          mass: weight,
          dimensions: {
            width: width / 100, // TO M
            height: height / 100,
            length: length / 100,
          },
          quantity,
          stackable: stackable === true,
        };
      }),
      requirements,
    },
    location: {
      ...dropOff.location,
    },
    receiver: dropOff.receiver,
    receiverPhone: dropOff.receiverPhone,
    sender: dropOff.sender,
    senderPhone: dropOff.senderPhone,
  };
};

export const getFormattedWeight = (quotingOptions, loads) => {
  const { type, equipment } = quotingOptions;
  if (QUOTING_TYPES.FTL === type) {
    const { capacity } = equipment;
    return {
      ...capacity,
    };
  }
  return {
    ...loads.items.totals.weight,
  };
};
export const getFormattedPickup = (pickup, locationKey = 'location') => {
  try {
    if (!pickup) return {};
    const { pickupDate = moment().toString() } = pickup;

    return {
      ...getFormattedAddress(pickup.address, locationKey),
      arrivalWindow: {
        begin: moment(pickupDate).unix(), // Need to calculate actually
        end: moment(pickupDate)
          .add(2, 'hours')
          .unix(),
      },
      duration: GOODS_LOADING_DURATION,
      identifier: `pickup${uuid()}`,
      description: 'Pickup ', // Should be free text
    };
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getFormattedDropoff = (dropoff, locationKey = 'location') => {
  try {
    // For now this is hard coded till we get the multistops
    const { pickupDate = moment().toString(), address } = dropoff;
    const formattedAddress = getFormattedAddress(address, locationKey);

    const result = {
      arrivalWindow: {
        begin: moment(pickupDate).unix(), // Need to calculate actually
        end: moment(pickupDate)
          .add(2, 'hours')
          .unix(),
      },
      duration: GOODS_UNLOADING_DURATION,
      identifier: `dropOff-${uuid()}`,
      description: 'Drop Off', // Should be free text
      load: dropoff.load,
      ...formattedAddress,
    };
    return result;
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getFormattedDropoffs = (
  dropoffs,
  locationKey = 'location',
  receivers = [],
  sender = {},
  quotingOptions,
) => {
  try {
    const { type, equipment } = quotingOptions;
    return dropoffs.map((dropoff, index) => {
      const {
        arrivalWindow,
        duration,
        identifier,
        description,
        load,
        address,
        coordinate,
      } = getFormattedDropoff(dropoff, locationKey);

      const formattedDropoff = {
        identifier,
        load,
        location: {
          address,
          arrivalWindow,
          coordinate,
          description,
          duration,
        },
      };

      if (receivers && receivers[index]) {
        const { contact: senderName, phone: senderPhone } = sender;
        const receiver = receivers[index];
        const { contact: receiverName, phone: receiverPhone } = receiver;
        return {
          ...formattedDropoff,
          sender: senderName,
          senderPhone,
          receiver: receiverName,
          receiverPhone,
        };
      }
      return formattedDropoff;
    });
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getFormattedLoads = (quotingOptions, dropoff, currency) => {
  const { equipment, type, description } = quotingOptions;
  const requirements = [];
  if (QUOTING_TYPES.FTL === type) {
    requirements.push(equipment.type);
  }

  return {
    identifier: uuid(), // Why I should send them all of the time ?
    description: description.join(','),
    items: getFormattedLoad(quotingOptions),
    requirements,
    ...getFormattedShipmentValue(quotingOptions, dropoff, currency),
  };
};

export const getFormattedLoad = (quotingOptions) => {
  const { load: allLoads, equipment } = quotingOptions;

  return allLoads.reduce((reduced, {
    type, dimensions, weight, quantity, stackable,
  }) => {
    const { width = 1, length = 1, height = 1 } = dimensions;
    const abstractType = type.startsWith('PALLET')
      ? 'PALLET'
      : type.startsWith('BOX')
        ? 'BOX'
        : 'OTHER';
    // TODO implement a util to convert and caclualte units
    reduced.push({
      type: abstractType,
      mass: weight,
      dimensions: {
        width: width / 100, // TO M
        height: height / 100,
        length: length / 100,
      },
      quantity,
      stackable: stackable === true,
    });

    return reduced;
  }, []);
  // switch (type) {
  //   case QUOTING_TYPES.FTL: {
  //     items.push({
  //       type: 'BOX',
  //       dimensions: { length: 1.2, width: 1, height: 1.8 },
  //       mass: equipment.capacity.value,
  //       stackable: true,
  //       quantity: 1,
  //     });
  //     break;
  //   }
  //   case QUOTING_TYPES.LTL: {
  //
  //   }
  // }

  return items;
};

export const getFormattedLoadsSummary = (quotingOptions, currency) => {
  const { equipment, value, description } = quotingOptions;
  const requirements = [];
  if (QUOTING_TYPES.FTL === quotingOptions.type) {
    requirements.push(equipment.type);
  }
  return {
    identifier: uuid(), // Why I should send them all of the time ?
    description: description.join(','),
    items: getFormattedLoadSummary(quotingOptions),
    value,
    currency,
    requirements,
  };
};

export const getFormattedLoadSummary = (quotingOptions) => {
  const { load: allLoads, type, equipment } = quotingOptions;
  const items = [];
  switch (type) {
    case QUOTING_TYPES.FTL: {
      items.push({
        type: 'BOX',
        dimensions: { length: 1.2, width: 1, height: 1.8 },
        mass: equipment.capacity ? equipment.capacity.value : 0,
        stackable: true,
        quantity: 1,
      });
      break;
    }
    case QUOTING_TYPES.LTL: {
      const formattedLoadSummary = allLoads.reduce(
        (reduced, {
          type, dimensions = {}, weight = 0, quantity = 0,
        }) => {
          const { width = 1, length = 1, height = 1 } = dimensions;
          const filtteredLoad = PREDEFINED_LOADS.filter((load) => load.value === type)[0];
          const volume = width * height * length * quantity;
          const totalWeight = weight * quantity;
          reduced.push({
            quantity,
            volume: {
              unit: 'cbm',
              value: volume,
            },
            weight: {
              unit: 'kg',
              value: totalWeight,
            },
            summary: `${quantity} X ${filtteredLoad.label} (${volume} cbm) - ${totalWeight}kg `,
          });

          return reduced;
        },
        [],
      );
      const totals = formattedLoadSummary.reduce(
        (reduced, item) => {
          const volume = reduced.volume.value + item.volume.value;
          const weight = reduced.weight.value + item.weight.value;
          reduced.volume.value = volume;
          reduced.weight.value = weight;
          reduced.quantity += parseInt(item.quantity);
          reduced.summary.push(item.summary);

          return reduced;
        },
        {
          quantity: 0,
          volume: {
            unit: 'cbm',
            value: 0,
          },
          weight: {
            unit: 'kg',
            value: 0,
          },
          summary: [],
        },
      );
      return {
        summary: formattedLoadSummary,
        totals,
      };
    }
  }

  return items;
};

export const getFormattedAddress = (addressObj, locationKey = 'location') => {
  try {
    if (!addressObj) return {};
    const [address] = addressObj;
    const {
      geometry: { location },
    } = address;
    let { lat: latitude, lng: longitude } = location;
    if (typeof latitude === 'function') {
      latitude = latitude();
    }
    if (typeof longitude === 'function') {
      longitude = longitude();
    }
    return {
      address: address.formatted_address,
      [locationKey]: { latitude, longitude },
    };
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getFormattedVolume = (quotingOptions) => ({
  value: 0,
  unit: 'CBM', // Change this
});
export const getFormattedItems = (quotingOptions) => ({});

export const getFormattedShippingSummary = (quotingOptions) => {
  const { equipment, load, type } = quotingOptions;
  if (type === QUOTING_TYPES.FTL) {
    const { type: equipmentType, capacity, truckSize: truckSizeFTL } = equipment;
    const truckSize = capacity && capacity.value ? capacity.value : truckSizeFTL;

    return {
      equipment: equipmentType,
      truckSize,
      quantity: load.length,
    };
  }
  return {};
};

export const getFormattedShipmentValue = (quotingOptions, dropoffs, currency) => {
  const { type, load } = quotingOptions;

  switch (type) {
    case QUOTING_TYPES.FTL: {
      const total = dropoffs.reduce((total, item) => {
        const { deliveryLoad = {} } = item;
        total += Number(deliveryLoad.shipmentValue);
        return total;
      }, 0);
      return {
        value: total,
        currency,
      };
    }
    case QUOTING_TYPES.LTL: {
      const total = load.reduce((total, item) => {
        total += Number(item.value);
        return total;
      }, 0);
      return {
        value: total,
        currency,
      };
    }
    default: {
      return {
        value: 0,
        currency,
      };
    }
  }
};
